import { useMemo } from 'react';
import { Order } from '@/model/Common.model';
import type { MeasurementComparatorProps, RenderFunctions } from './useMeasurementComparator.model';
import {
  filterMeasurementsToDate,
  findComparatorDateMeasurement,
  findDifference,
  findFirstMeasurement,
  findLatestMeasurement,
  findMaximumMeasurement,
  orderMeasurementsByDate,
} from './useMeasurementComparator.utils';
import type { Measurement } from '@/model/Measurement.model.ts';

const createComparator =
  (measurement: Measurement | null, key: string) =>
  (value: number | null): number | null =>
    findDifference(value, measurement?.variables?.[key] ?? null);

const useMeasurementComparator = ({
  calculations = [],
  measurements = [],
  comparatorDate,
  measuredAtDate,
}: MeasurementComparatorProps) => {
  return useMemo(() => {
    const ascMeasurements = orderMeasurementsByDate(measurements, Order.asc);
    const measurementsToDate = filterMeasurementsToDate(ascMeasurements, measuredAtDate);

    return calculations.reduce<Record<string, RenderFunctions>>((acc, { id: calculationKey }) => {
      const firstMeasurement = findFirstMeasurement(measurementsToDate, calculationKey);
      const latestMeasurement = findLatestMeasurement(measurementsToDate, calculationKey);
      const maximumMeasurement = findMaximumMeasurement(measurementsToDate, calculationKey);
      const comparatorMeasurement = findComparatorDateMeasurement(ascMeasurements, {
        comparatorDate,
        measurementKey: calculationKey,
      });

      acc[calculationKey] = {
        comparatorDate: createComparator(comparatorMeasurement, calculationKey),
        fromFirst: createComparator(firstMeasurement, calculationKey),
        fromLatest: createComparator(latestMeasurement, calculationKey),
        fromMaximum: createComparator(maximumMeasurement, calculationKey),
      };

      return acc;
    }, {});
  }, [calculations, measurements, comparatorDate, measuredAtDate]);
};

export default useMeasurementComparator;
